import { I18nT } from 'types'

const CommonFunction = class CommonFunction {
  static extractS3KeyFromUrl(s3Url: string) {
    if (!s3Url) return null
    if (!s3Url.startsWith('http://') && !s3Url.startsWith('https://'))
      return s3Url
    try {
      const url = new URL(s3Url)
      let s3Key = url.pathname

      // Remove the leading slash if it exists
      if (s3Key.startsWith('/')) {
        s3Key = s3Key.substring(1)
      }

      return s3Key
    } catch (error) {
      return null
    }
  }

  static getBreadcrumbTextByFolderType = (folderType: string, t: I18nT) => {
    return t(`building.folderType.${folderType}`)
  }

  static getBreadcrumbTextByChidType = (
    type: string,
    t: I18nT,
    currentBuildingType: number
  ) => {
    if (currentBuildingType === 20) {
      return t(`building.folderType.childbcp.${type?.slice(-1)}`)
    }
    if (currentBuildingType === 30) {
      return t(`building.folderType.childrn.${type?.slice(-1)}`)
    }
    return t(`building.folderType.child360.${type?.slice(-1)}`)
  }

  static FetchTourLocationContentFromS3Link = async (s3Link: string) => {
    // Check if valid HTTP/HTTPS URL
    if (
      s3Link &&
      (s3Link.startsWith('http://') || s3Link.startsWith('https://'))
    ) {
      const response = await fetch(s3Link)
      const blob = await response.blob()
      const textContent = await blob.text()
      return textContent
    }
    // For non-S3 links, just return the content as is
    return s3Link
  }

  static extractS3Key(value?: string) {
    if (!value) return ''
    try {
      const url = new URL(value)
      return url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname
    } catch {
      return value
    }
  }

  /**
   * Extracts or converts a URL to a Google Maps Embed URL.
   * Handles iframe HTML strings, full Google Maps links, short links, and direct URLs.
   * @param value - The input value (iframe code, full link, short link, or embed link)
   * @returns Embed URL string or null if invalid/unsupported
   */
  static getEmbedUrl(value?: string): string | null {
    if (!value) return null

    // Case 0: Already an embed URL (has output=embed) - return as is
    if (value.includes('output=embed')) {
      return value
    }

    // Case 1: Iframe HTML string (extract src - support both single and double quotes)
    const iframeMatch = value.match(/src=["']([^"']+)["']/)
    if (iframeMatch) return iframeMatch[1]

    // Case 2: Short Link (goo.gl) - Cannot convert client-side due to CORS
    if (value.includes('maps.app.goo.gl')) {
      // Short links cannot be expanded client-side
      // Return null to show tutorial/placeholder in UI
      return null
    }

    // Case 3: Full Google Maps Link -> Convert to Embed Link
    if (
      value.includes('google.com/maps') ||
      value.includes('maps.google.com')
    ) {
      // Priority 1: Extract coordinates from URL (most stable for embed)
      // Format: @lat,long,zoom (e.g., @21.0467648,105.7864645,16z)
      const coordMatch = value.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/)
      if (coordMatch) {
        return `https://maps.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&output=embed`
      }

      // Priority 2: Extract 'place' query (e.g., .../maps/place/My+Place/@...)
      const placeMatch = value.match(/\/place\/([^/@]+)/)
      if (placeMatch) {
        // Ensure proper encoding
        const query = encodeURIComponent(decodeURIComponent(placeMatch[1]))
        return `https://maps.google.com/maps?q=${query}&output=embed`
      }

      // Priority 3: Extract 'search' query (e.g., .../maps/search/coffeeshop/...)
      const searchMatch = value.match(/\/search\/([^/@?]+)/)
      if (searchMatch) {
        // Ensure proper encoding for special characters (Vietnamese, etc.)
        const query = encodeURIComponent(decodeURIComponent(searchMatch[1]))
        return `https://maps.google.com/maps?q=${query}&output=embed`
      }

      // Priority 4: Extract 'q' query parameter (e.g., ...?q=My+Place...)
      const queryMatch = value.match(/[?&]q=([^&]+)/)
      if (queryMatch) {
        const query = encodeURIComponent(decodeURIComponent(queryMatch[1]))
        return `https://maps.google.com/maps?q=${query}&output=embed`
      }
    }

    // Case 4: Direct http(s) link (not Google Maps, just return as is)
    if (value.startsWith('http')) return value

    return null
  }
}

export default CommonFunction
